import { AppDispatch } from '@/app/store';
import { clearFormState } from '@/services/createFormSlice';
import { letterApiSlice } from '@/services/api/letterApiSlice';
import { useAppDispatch } from '@/app/hooks';
import { useCallback } from 'react';

/**
 * Discards the create form, and lets the caller wait until the server has answered the drops of its staged photos.
 * <p>
 * The drops are sent by `stagedPhotoDropMiddleware` while the form is cleared, not by this hook.
 *
 * @return The function that clears the form, returning the promise of the drops still running.
 * The form is cleared before that promise settles, so a caller that only moves the sender on
 * does not have to wait for work whose failure it would ignore anyway.
 * Await it only before staging something new, or the drops race the new uploads
 * and a sender near the allowance is refused by their own abandoned rows.
 */
export const useClearCreateForm = (): (() => Promise<void>) => {
  const dispatch: AppDispatch = useAppDispatch();

  return useCallback(async (): Promise<void> => {
    dispatch(clearFormState());
    await Promise.all(
      dispatch(letterApiSlice.util.getRunningMutationsThunk()).filter(
        (mutation): boolean => mutation.arg.endpointName === letterApiSlice.endpoints.deleteStagedPhoto.name,
      ),
    );
  }, [dispatch]);
};
